import type { FormField } from "@tellescope/types-models";
import { advanceWhile, collectFieldPath, MAX_FIELD_PATH_DEPTH, type FieldPathWalkContext } from "./walk-field-path";
import { convertFormFieldType } from "./convert-form-field-type";
import { getNextField } from "./get-next-field";
import { isAnsweredQuestionGroup, isPassThroughField } from "./question-group";
import type { FormFieldNode } from "./types";

export interface ResolveInitialFieldStackArgs {
  /** Root of the field graph */
  fieldTree: FormFieldNode;
  /** All fields by id, used to resolve Question Group sub-field types */
  fieldsMap: Record<string, FormField | undefined>;
  /** Ids answered this session */
  answeredFieldIds: ReadonlySet<string>;
  /** Ids answered by a previously saved response */
  existingResponseFieldIds: ReadonlySet<string>;
  /**
   * Field to resume at. Fields before it are walked past regardless of whether
   * they were answered; when omitted, the walk is "already past" from the root.
   */
  startingFieldId?: string;
  walkContext: FieldPathWalkContext;
}

/**
 * The stack a returning enduser opens on: every node walked past, topped by the first field that
 * still needs them.
 *
 * The walk steps forward from the root while a field is skippable — already answered (directly, or
 * every sub-field of a Question Group), pass-through (renders no UI), display-only with an answer
 * later in the path, or positioned before `startingFieldId` — and comes to rest on the first field
 * that is none of those. Display-only fields are only skipped when something *after* them was
 * answered, so a form opening on an intro screen does not jump past it. Pass-through nodes stay on
 * the stack: they were walked, and their answers count.
 *
 * Applied once on mount; not a live projection of the answers.
 */
export const resolveInitialFieldStack = ({
  fieldTree,
  fieldsMap,
  answeredFieldIds,
  existingResponseFieldIds,
  startingFieldId,
  walkContext,
}: ResolveInitialFieldStackArgs): FormFieldNode[] => {
  const wasPreviouslySaved = (field: FormField) =>
    answeredFieldIds.has(field.id) ||
    existingResponseFieldIds.has(field.id) ||
    isAnsweredQuestionGroup(field, answeredFieldIds, existingResponseFieldIds);

  const path = collectFieldPath(fieldTree, walkContext);
  const hasResponseAfter = path.reduceRight<{ map: Record<string, boolean>; seenResponse: boolean }>(
    (acc, node) => ({
      map: { ...acc.map, [node.value.id]: acc.seenResponse },
      seenResponse: acc.seenResponse || wasPreviouslySaved(node.value),
    }),
    { map: {}, seenResponse: false }
  ).map;

  const skipVisited = (
    current: FormFieldNode,
    walked: FormFieldNode[],
    hasPassedStartingField: boolean,
    depth: number
  ): FormFieldNode[] => {
    if (depth >= MAX_FIELD_PATH_DEPTH) return [...walked, current];

    const passedStartingField =
      hasPassedStartingField ||
      current.value.id === startingFieldId ||
      walked.some((f) => f.value.id === startingFieldId);

    const skippableDisplayOnly =
      convertFormFieldType(current.value.type) === null && hasResponseAfter[current.value.id];
    const isSkippable =
      skippableDisplayOnly ||
      isPassThroughField(current.value, fieldsMap) ||
      wasPreviouslySaved(current.value) ||
      !passedStartingField;
    if (!isSkippable) return [...walked, current];

    const next = getNextField(
      current,
      walkContext.responsesByFieldId[current.value.id],
      walkContext.allResponses,
      walkContext.logicOptions
    );
    if (!next) return [...walked, current];

    return skipVisited(next, [...walked, current], passedStartingField, depth + 1);
  };

  const visited = skipVisited(fieldTree, [], !startingFieldId, 0);
  const resting = visited[visited.length - 1];
  if (!resting) return visited;

  const settled = advanceWhile(resting, (node) => isPassThroughField(node.value, fieldsMap), walkContext);
  return [...visited.slice(0, -1), ...settled.skipped, settled.node];
};
